import {
  dynamicIconImports,
  type IconName as LucideDynamicIconName,
} from 'lucide-react/dynamic';

import type {
  IconColorMode,
  IconRegistryEntry,
  IconRegistryMap,
} from '@/hen-ui/providers/IconRegistry';

const SVG_DATA_URL = /^data:image\/svg\+xml;base64,[A-Za-z0-9+/]+={0,2}$/;

/**
 * Returns a URL that is safe to place inside CSS `url("…")` and `<img src>`,
 * or `null`. Base64 keeps data URLs free of quotes; `URL#href` percent-encodes
 * quotes and whitespace in `https:` URLs but leaves backslashes in the query
 * and fragment, and a backslash is a CSS escape, so those are encoded here.
 */
export function toSafeIconUrl(url: unknown): string | null {
  if (typeof url !== 'string') {
    return null;
  }
  if (SVG_DATA_URL.test(url)) {
    return url;
  }
  try {
    const parsed = new URL(url);
    return parsed.protocol === 'https:'
      ? parsed.href.replace(/\\/g, '%5C')
      : null;
  } catch {
    return null;
  }
}

export function isLucideIconName(name: string): name is LucideDynamicIconName {
  return (
    Object.prototype.hasOwnProperty.call(dynamicIconImports, name) &&
    typeof dynamicIconImports[name as LucideDynamicIconName] === 'function'
  );
}

export type ResolvedIcon =
  | { kind: 'lucide'; name: LucideDynamicIconName }
  | { kind: 'registry'; entry: IconRegistryEntry }
  | { kind: 'empty' };

function isColorMode(value: unknown): value is IconColorMode {
  return value === 'mono' || value === 'original';
}

/**
 * Lucide kebab first (bare slugs stay Lucide even if a pack reused the name),
 * then the host registry, else empty. Unknown `custom/{slug}` must not crash
 * or log as an invalid Lucide name.
 */
export function resolveIconSource(
  icon: string,
  registry: IconRegistryMap | null,
): ResolvedIcon {
  if (isLucideIconName(icon)) {
    return { kind: 'lucide', name: icon };
  }

  const entry = registry?.[icon];
  const url = toSafeIconUrl(entry?.url);
  if (entry && url && isColorMode(entry.colorMode)) {
    return { kind: 'registry', entry: { url, colorMode: entry.colorMode } };
  }

  return { kind: 'empty' };
}
